// Pure position changes. Every function returns a new GameState (structural sharing) or `null` when
// the action is not allowed. Timers, animation phases and the busy/idle status live in controller.ts.
// Undo = restore the snapshot taken before the action (no per-move inverse logic).
import type { CardKey, GameId, GameState, PileRef, Piles, Scoring, ScoreKind, Snapshot, Suit } from './types';
import { buildDeck, buildTileDeck } from './cards';
import { patternForOrder, shuffle } from './shuffle';
import { RULES, suitsFromPattern, type VariantRules } from './rules';
import { card, pileCards, topOf } from './rules/base';
import { HINT_PENALTY, POINTS, TICK_PENALTY, clampMoves, clampScore, penaltyTicksBetween } from './scoring';

export const rulesOf = (s: GameState): VariantRules => RULES[s.game];

/** A game of `id` with its deck built and nothing dealt (all cards on the stock, face down). */
export function createGameState(id: GameId, opts: { scoring: Scoring; autoFlip: boolean; layout?: number }): GameState {
  const rules = RULES[id];
  const deck = rules.tiles ? buildTileDeck() : buildDeck(rules.suits, rules.decks);
  return {
    game: id,
    deck,
    suits: rules.suits,
    pattern: [],
    isRetry: false,
    scoring: opts.scoring,
    autoFlip: opts.autoFlip,
    piles: {
      stock: deck.map((c) => c.key).reverse(),
      waste: [],
      foundations: Array.from({ length: rules.foundations }, () => []),
      tableau: rules.columns.map(() => []),
      cells: Array.from({ length: rules.cells }, () => []),
    },
    faceUp: deck.map(() => false),
    score: 0,
    moves: 0,
    seconds: 0,
    timerRunning: false,
    recycles: 0,
    wasteShown: 0,
    history: [],
    won: false,
    hints: 0,
    layout: rules.tiles ? (opts.layout ?? 0) : 0,
  };
}

/**
 * Shuffle and deal (deck.js:101-173): column k takes the next `columns[k]` cards from the front of
 * the shuffled order (first taken = bottom); the last card of each column is face up (all of them in
 * Forty Thieves); the rest is the stock, `order[dealt]` being the first card turned.
 */
export function deal(s: GameState, pattern: readonly number[] | null, rng: () => number, opts?: { retry?: boolean; scoring?: Scoring; autoFlip?: boolean; suits?: readonly Suit[] }): GameState {
  const rules = rulesOf(s);
  // Mahjong: a solvable deal, as the pattern that deals it (Retry and the stored wins replay it like any other)
  if (!pattern && rules.dealIds) {
    const base = s.deck.map((c) => c.id);
    pattern = patternForOrder(base, rules.dealIds(base, rng, s.layout));
  }
  const { order, pattern: used } = shuffle(s.deck.length, pattern, rng);
  // Spider one/two suits: the deck's suits come from the pattern (or are given, e.g. Retry / tests).
  let deck = s.deck;
  let suits = s.suits;
  if (rules.variableSuits) {
    suits = opts?.suits ?? suitsFromPattern(used, rules.variableSuits);
    if (suits.join() !== s.suits.join()) deck = buildDeck(suits, rules.decks);
  }
  const faceUp = deck.map(() => false);
  let pos = 0;
  const tableau = rules.columns.map((n) => {
    const col = order.slice(pos, pos + n);
    pos += n;
    if (rules.allFaceUp) for (const k of col) faceUp[k] = true;
    else if (col.length) faceUp[col[col.length - 1]!] = true;
    return col;
  });
  const rest = order.slice(pos);
  // Pyramid: the stock's top card lies face up (the next card turned is rest[0])
  if (rules.stockTopUp && rest.length) faceUp[rest[0]!] = true;
  return {
    ...s,
    deck,
    suits,
    // Mahjong: the layout after the tiles' pattern (controller.ts startDeal reads it back)
    pattern: rules.tiles ? [...used, s.layout] : used,
    isRetry: opts?.retry === true,
    scoring: opts?.scoring ?? s.scoring,
    autoFlip: opts?.autoFlip ?? s.autoFlip,
    piles: {
      stock: rest.slice().reverse(), // bottom first; the top (last) is the next card turned
      waste: [],
      foundations: Array.from({ length: rules.foundations }, () => []),
      tableau,
      cells: Array.from({ length: rules.cells }, () => []),
    },
    faceUp,
    score: 0,
    moves: 0,
    seconds: 0,
    timerRunning: false,
    recycles: 0,
    wasteShown: 0,
    history: [],
    won: false,
    hints: 0,
  };
}

const snapshotOf = (s: GameState): Snapshot => ({ piles: s.piles, faceUp: s.faceUp, score: s.score, moves: s.moves, seconds: s.seconds, recycles: s.recycles, wasteShown: s.wasteShown, hints: s.hints ?? 0 });

/** Visible waste cards after a card left the waste (see GameState.wasteShown). */
/** After a card leaves the waste up to 3 cards are shown again (legacy KL-S03, FT-033): fanned in turn-three, stacked otherwise. */
function wasteShownAfterPlay(_s: GameState, wasteLength: number): number {
  return Math.min(3, Math.max(0, wasteLength));
}

function withPile(piles: Piles, ref: PileRef, cards: readonly CardKey[]): Piles {
  switch (ref.kind) {
    case 'stock':
      return { ...piles, stock: cards };
    case 'waste':
      return { ...piles, waste: cards };
    case 'foundation':
      return { ...piles, foundations: piles.foundations.map((p, i) => (i === ref.index ? cards : p)) };
    case 'tableau':
      return { ...piles, tableau: piles.tableau.map((p, i) => (i === ref.index ? cards : p)) };
    case 'cell':
      return { ...piles, cells: piles.cells.map((p, i) => (i === ref.index ? cards : p)) };
  }
}

export const samePile = (a: PileRef, b: PileRef): boolean => a.kind === b.kind && (a.kind === 'stock' || a.kind === 'waste' || a.index === (b as { index: number }).index);

/** All cards are on the foundations (Pyramid: the pyramid is cleared). Checked after every successful action. */
export function isWin(s: GameState): boolean {
  const rules = rulesOf(s);
  if (rules.isWon) return rules.isWon(s);
  return s.piles.foundations.reduce((n, p) => n + p.length, 0) === s.deck.length;
}

/** Pyramid: the stock's top card face up (after anything that took it, or gave the stock a new top). */
function stockTopUp(s: GameState, faceUp: readonly boolean[]): readonly boolean[] {
  const top = topOf(s.piles.stock);
  return rulesOf(s).stockTopUp && top !== undefined && !faceUp[top] ? setFaceUp(faceUp, [top], true) : faceUp;
}

/** Cards that still need to reach a foundation (for the auto-move "no face-down card" check). */
export function hasFaceDownCard(s: GameState): boolean {
  return s.piles.tableau.some((col) => col.some((k) => !s.faceUp[k]));
}

function setFaceUp(faceUp: readonly boolean[], keys: readonly CardKey[], up: boolean): boolean[] {
  const out = faceUp.slice();
  for (const k of keys) out[k] = up;
  return out;
}

export function canLift(s: GameState, pile: PileRef, index: number): boolean {
  if (s.won) return false;
  return rulesOf(s).canLift(s, pile, index);
}

export function canDrop(s: GameState, from: { pile: PileRef; index: number }, to: PileRef): boolean {
  if (s.won || samePile(from.pile, to)) return false;
  const rules = rulesOf(s);
  if (!rules.canLift(s, from.pile, from.index)) return false;
  const run = pileCards(s, from.pile).slice(from.index);
  if (to.kind === 'tableau' && from.pile.kind === 'foundation' && !rules.foundationToTableau) return false;
  return rules.canDrop(s, run, to);
}

/** Every pile the run at `from` may legally go to (for highlighting and tests). */
export function legalTargets(s: GameState, from: { pile: PileRef; index: number }): PileRef[] {
  const out: PileRef[] = [];
  s.piles.tableau.forEach((_, index) => {
    if (canDrop(s, from, { kind: 'tableau', index })) out.push({ kind: 'tableau', index });
  });
  s.piles.foundations.forEach((_, index) => {
    if (canDrop(s, from, { kind: 'foundation', index })) out.push({ kind: 'foundation', index });
  });
  s.piles.cells.forEach((_, index) => {
    if (canDrop(s, from, { kind: 'cell', index })) out.push({ kind: 'cell', index });
  });
  // Pyramid: the waste's and the stock's top cards are partners too
  if (rulesOf(s).pairs) for (const ref of [{ kind: 'waste' }, { kind: 'stock' }] as const) if (canDrop(s, from, ref)) out.push(ref);
  return out;
}

function scoreKind(s: GameState, from: PileRef, to: PileRef, runBottom: CardKey, fromIndex: number): ScoreKind {
  if (rulesOf(s).pairs) return to.kind === 'foundation' ? 'king' : 'pair';
  if (from.kind === 'waste') return to.kind === 'foundation' ? 'waste_to_foundation' : 'waste_to_tableau';
  if (from.kind === 'foundation') return to.kind === 'foundation' ? 'foundation_to_foundation' : 'foundation_to_tableau';
  if (to.kind === 'foundation') return 'tableau_to_foundation';
  // FreeCell: parking a card in a free cell, or taking it back, scores nothing (it could be done for ever)
  if (from.kind === 'cell' || to.kind === 'cell') return 'none';
  // tableau -> tableau: +5 only when the moved card leaves a face-down card, or a card it does not
  // build on (.ai/game-rules.md "Point table"); 0 when it was the first card of its column.
  const col = pileCards(s, from);
  const under = fromIndex > 0 ? col[fromIndex - 1] : undefined;
  if (under === undefined) return 'none';
  if (!s.faceUp[under]) return 'tableau_to_tableau';
  return rulesOf(s).buildsOn(card(s, runBottom), card(s, under)) ? 'none' : 'tableau_to_tableau';
}

/** After a successful action: auto-flip the uncovered tableau card, collect Spider runs, detect the win. */
function finishAction(s: GameState, touched: PileRef[], snapshot: Snapshot, scored: number, countsMove: boolean): GameState {
  const rules = rulesOf(s);
  let faceUp = s.faceUp;
  let piles = s.piles;
  if (s.autoFlip) {
    for (const ref of touched) {
      if (ref.kind !== 'tableau') continue;
      const top = topOf(pileCards({ ...s, piles }, ref));
      if (top !== undefined && !faceUp[top]) faceUp = setFaceUp(faceUp, [top], true);
    }
  }
  // Spider: a complete K..A same-suit run goes to the next empty foundation by itself (no points, no move).
  let next: GameState = { ...s, piles, faceUp };
  for (const ref of touched) {
    if (ref.kind !== 'tableau') continue;
    while (rules.completedRun(next, ref.index)) {
      const col = next.piles.tableau[ref.index]!;
      const run = col.slice(-13);
      const slot = next.piles.foundations.findIndex((f) => f.length === 0);
      if (slot < 0) break;
      piles = withPile(withPile(next.piles, ref, col.slice(0, -13)), { kind: 'foundation', index: slot }, run);
      let fu = next.faceUp;
      const newTop = topOf(piles.tableau[ref.index]!);
      if (next.autoFlip && newTop !== undefined && !fu[newTop]) fu = setFaceUp(fu, [newTop], true);
      next = { ...next, piles, faceUp: fu };
    }
  }
  next = { ...next, faceUp: stockTopUp(next, next.faceUp) };
  const won = isWin(next);
  return {
    ...next,
    score: clampScore(next.score + scored),
    moves: countsMove ? clampMoves(next.moves + 1) : next.moves,
    history: [...s.history, snapshot],
    won,
  };
}

/** Drag-and-drop (or any) move of the run starting at `from` onto `to`. */
export function moveRun(s: GameState, from: { pile: PileRef; index: number }, to: PileRef): GameState | null {
  if (!canDrop(s, from, to)) return null;
  const src = pileCards(s, from.pile);
  const run = src.slice(from.index);
  const kind = scoreKind(s, from.pile, to, run[0]!, from.index);
  const snapshot = snapshotOf(s);
  if (kind === 'pair') {
    // Pyramid: the card it was dropped on goes to the discard pile first, the dropped card on top of it
    const dst = pileCards(s, to);
    const home = { kind: 'foundation', index: 0 } as const;
    let piles = withPile(withPile(s.piles, from.pile, src.slice(0, -1)), to, dst.slice(0, -1));
    piles = withPile(piles, home, [...piles.foundations[0]!, dst[dst.length - 1]!, run[0]!]);
    const shown = from.pile.kind === 'waste' || to.kind === 'waste' ? wasteShownAfterPlay(s, piles.waste.length) : s.wasteShown;
    return finishAction({ ...s, piles, wasteShown: shown }, [from.pile, to, home], snapshot, POINTS[kind], true);
  }
  const piles = withPile(withPile(s.piles, from.pile, src.slice(0, from.index)), to, [...pileCards(s, to), ...run]);
  const wasteShown = from.pile.kind === 'waste' ? wasteShownAfterPlay(s, piles.waste.length) : s.wasteShown;
  return finishAction({ ...s, piles, wasteShown }, [from.pile, to], snapshot, POINTS[kind], true);
}

/** Double-click / tap / auto-move: send the single top card at `ref` to the foundation that takes it. */
export function sendToFoundation(s: GameState, ref: { pile: PileRef; index: number }): GameState | null {
  if (s.won) return null;
  const rules = rulesOf(s);
  const cards = pileCards(s, ref.pile);
  if (ref.pile.kind === 'foundation' || ref.pile.kind === 'stock' || ref.index !== cards.length - 1) return null;
  const key = cards[ref.index];
  if (key === undefined || !rules.canLift(s, ref.pile, ref.index)) return null;
  const target = rules.foundationFor(s, key);
  if (target !== null) return moveRun(s, ref, { kind: 'foundation', index: target });
  // FreeCell, as in the Windows game (owner 2026-10-23): a column's card no foundation takes goes to the first
  // empty free cell
  if (ref.pile.kind === 'tableau' && s.piles.cells.length) {
    const cell = s.piles.cells.findIndex((c) => c.length === 0);
    if (cell >= 0) return moveRun(s, ref, { kind: 'cell', index: cell });
  }
  return null;
}

/** The next card auto-move would send home, or null when there is none (so it can never hang). */
export function nextFoundationMove(s: GameState): { pile: PileRef; index: number } | null {
  const rules = rulesOf(s);
  if (!rules.autoMove || s.won) return null;
  for (let i = 0; i < s.piles.tableau.length; i++) {
    const col = s.piles.tableau[i]!;
    if (!col.length) continue;
    const top = col[col.length - 1]!;
    if (s.faceUp[top] && rules.foundationFor(s, top) !== null) return { pile: { kind: 'tableau', index: i }, index: col.length - 1 };
  }
  const w = s.piles.waste;
  if (w.length && rules.foundationFor(s, w[w.length - 1]!) !== null) return { pile: { kind: 'waste' }, index: w.length - 1 };
  for (let i = 0; i < s.piles.cells.length; i++) {
    const k = s.piles.cells[i]![0];
    if (k !== undefined && rules.foundationFor(s, k) !== null) return { pile: { kind: 'cell', index: i }, index: 0 };
  }
  return null;
}

/**
 * A click on the stock. Klondike: turn 1 or 3 cards to the waste; an empty stock takes the waste back
 * (recycle) and turns the first card(s) at once, as ONE undo step (KL-S06). Spider: deal one card to
 * every column. Forty Thieves: turn one card, no recycle. Returns null when nothing happens
 * (empty stock without a recycle, both piles empty).
 */
export function clickStock(s: GameState): GameState | null {
  if (s.won) return null;
  const rules = rulesOf(s);
  const snapshot = snapshotOf(s);
  let piles = s.piles;
  let recycles = s.recycles;
  if (!piles.stock.length) {
    if (!rules.recycle || !piles.waste.length) return null;
    if (rules.maxRecycles !== undefined && recycles >= rules.maxRecycles) return null;
    // The first card turned again is the first one that was turned before (the bottom of the waste).
    piles = { ...piles, stock: piles.waste.slice().reverse(), waste: [] };
    recycles += 1;
    const faceUp = setFaceUp(s.faceUp, piles.stock, false);
    s = { ...s, piles, faceUp };
    // Pyramid: the stock's top card is in play face up, so the recycle stops here (no card turned by itself)
    if (rules.stockTopUp) return { ...s, faceUp: stockTopUp(s, faceUp), recycles, wasteShown: 0, history: [...s.history, snapshot] };
  }
  if (rules.turn === 'spider') {
    const n = Math.min(s.piles.tableau.length, piles.stock.length);
    const dealt = piles.stock.slice(-n).reverse(); // the top of the stock goes to column 0
    const tableau = piles.tableau.map((col, i) => (i < n ? [...col, dealt[i]!] : col));
    piles = { ...piles, stock: piles.stock.slice(0, -n), tableau };
    const next: GameState = { ...s, piles, faceUp: setFaceUp(s.faceUp, dealt, true), recycles };
    const touched: PileRef[] = tableau.map((_, index) => ({ kind: 'tableau', index }));
    // Spider never wins on a deal, but a dealt card may complete a run (collected here).
    return finishAction(next, touched, snapshot, 0, true);
  }
  const n = Math.min(rules.turn, piles.stock.length);
  const turned = piles.stock.slice(-n).reverse(); // first turned = top of the stock; the last turned lands on top of the waste
  piles = { ...piles, stock: piles.stock.slice(0, -n), waste: [...piles.waste, ...turned] };
  const recycled = recycles !== s.recycles;
  const next = { ...s, piles };
  return {
    ...next,
    faceUp: stockTopUp(next, setFaceUp(s.faceUp, turned, true)),
    recycles,
    wasteShown: rules.turn === 3 ? n : Math.min(3, piles.waste.length),
    // A recycle (with its automatic first turn) is free and not a move (game-rules.md "Recycle").
    moves: recycled ? s.moves : clampMoves(s.moves + 1),
    history: [...s.history, snapshot],
  };
}

/** Mahjong's shuffle (owner 2026-10-24: free when no move is left): the tiles on the board change places, solvable
 *  when it can be. One undo step; no move, no points. */
export function reshuffle(s: GameState, rng: () => number): GameState | null {
  const shuffled = rulesOf(s).reshuffle;
  if (s.won || !shuffled) return null;
  return { ...s, piles: { ...s.piles, tableau: shuffled(s, rng) }, history: [...s.history, snapshotOf(s)] };
}

/** Mahjong's hint: one free matching pair (place indexes) and the position after its penalty (-10, no move, no undo
 *  step), or null when no pair is left (no penalty: the "No moves" window opens instead). */
export function takeHint(s: GameState): { pair: [number, number]; state: GameState } | null {
  const pairsLeft = rulesOf(s).pairsLeft;
  if (s.won || !pairsLeft) return null;
  const pair = pairsLeft(s)[0];
  if (!pair) return null;
  return { pair, state: { ...s, hints: (s.hints ?? 0) + 1, score: clampScore(s.score - HINT_PENALTY) } };
}

/** Click on a face-down top tableau card (auto-flip off): turn it. Free: no move, no score, no history. */
export function flipTop(s: GameState, column: number): GameState | null {
  if (s.won) return null;
  const top = topOf(s.piles.tableau[column] ?? []);
  if (top === undefined || s.faceUp[top]) return null;
  return { ...s, faceUp: setFaceUp(s.faceUp, [top], true) };
}

/**
 * Undo: restore the last snapshot. Score = the score before that action minus the timed penalties
 * that ticked since (not refunded: legacy rule, types/klondike.js:468-476). Time is not rewound.
 * Moves are never lowered (product decision). Nothing after the win (history frozen).
 */
export function undo(s: GameState): GameState | null {
  if (s.won) return null;
  const snap = s.history[s.history.length - 1];
  if (!snap) return null;
  const penalty = (s.scoring === 'timed' ? TICK_PENALTY * penaltyTicksBetween(snap.seconds, s.seconds) : 0) + HINT_PENALTY * ((s.hints ?? 0) - (snap.hints ?? 0));
  return {
    ...s,
    piles: snap.piles,
    faceUp: snap.faceUp,
    score: clampScore(snap.score - penalty),
    recycles: snap.recycles,
    wasteShown: snap.wasteShown,
    history: s.history.slice(0, -1),
  };
}

/** The game clock moved to `seconds`; timed scoring loses 2 points at every full 10 s. */
export function setSeconds(s: GameState, seconds: number): GameState {
  if (seconds === s.seconds) return s;
  const ticks = s.scoring === 'timed' && seconds > s.seconds ? penaltyTicksBetween(s.seconds, seconds) : 0;
  return { ...s, seconds, score: ticks ? clampScore(s.score - TICK_PENALTY * ticks) : s.score };
}

export const setTimerRunning = (s: GameState, running: boolean): GameState => (s.timerRunning === running ? s : { ...s, timerRunning: running });

export const setAutoFlip = (s: GameState, autoFlip: boolean): GameState => (s.autoFlip === autoFlip ? s : { ...s, autoFlip });

/** Suits actually present in the deck (Spider one/two suits), in first-seen order. */
export function suitsInDeck(s: GameState): Suit[] {
  const out: Suit[] = [];
  for (const c of s.deck) if (!out.includes(c.suit)) out.push(c.suit);
  return out;
}
